import React, { useState } from 'react';
import { Box, Paper, Typography, TextField, Button, Grid, Chip } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import FilterListIcon from '@mui/icons-material/FilterList';

import { dnsLogs, aiDomains } from '../data/mockData';

const DNSLogs = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredLogs, setFilteredLogs] = useState(dnsLogs);

  // Define columns for the data grid
  const columns = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'timestamp', headerName: 'Timestamp', width: 200, 
      valueFormatter: (params) => new Date(params.value).toLocaleString() 
    },
    { field: 'sourceIP', headerName: 'Source IP', width: 150 },
    { field: 'username', headerName: 'Username', width: 130 },
    { field: 'department', headerName: 'Department', width: 150 },
    { 
      field: 'domain', 
      headerName: 'Domain', 
      width: 200,
      renderCell: (params) => {
        // Find if this domain is in our AI domains list
        const isAIDomain = aiDomains.some(domain => domain.domain === params.value);
        
        return (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {params.value}
            {isAIDomain && 
              <Chip 
                label="AI" 
                color="primary" 
                size="small" 
                sx={{ ml: 1 }} 
              />
            }
          </Box>
        );
      }
    },
    { field: 'queryType', headerName: 'Query Type', width: 120 },
  ];

  // Handle search
  const handleSearch = () => {
    if (searchTerm === '') {
      setFilteredLogs(dnsLogs);
      return;
    }
    
    const lowercasedSearch = searchTerm.toLowerCase();
    
    const filtered = dnsLogs.filter(log => 
      log.sourceIP.toLowerCase().includes(lowercasedSearch) ||
      log.domain.toLowerCase().includes(lowercasedSearch) ||
      log.username.toLowerCase().includes(lowercasedSearch) ||
      log.department.toLowerCase().includes(lowercasedSearch)
    );
    
    setFilteredLogs(filtered);
  };

  // Handle search input changes
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Handle search on Enter key
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Mock function for download logs
  const handleDownload = () => {
    alert('Download functionality would be implemented here in a real application.');
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        DNS Logs
      </Typography>
      
      <Paper sx={{ p: 2, mb: 3, bgcolor: 'background.paper' }}>
        <Typography variant="h6" gutterBottom>
          Log Search & Filters
        </Typography>
        
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              variant="outlined"
              label="Search by IP, Domain, Username, or Department"
              value={searchTerm}
              onChange={handleSearchChange}
              onKeyPress={handleKeyPress}
              InputProps={{
                endAdornment: (
                  <Button 
                    variant="contained" 
                    color="primary" 
                    onClick={handleSearch}
                    startIcon={<SearchIcon />}
                  >
                    Search
                  </Button>
                ),
              }}
            />
          </Grid>
          
          <Grid item>
            <Button 
              variant="outlined" 
              startIcon={<FilterListIcon />}
              sx={{ mr: 1 }}
            >
              Advanced Filters
            </Button>
            
            <Button 
              variant="outlined" 
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
            >
              Export Logs
            </Button>
          </Grid>
        </Grid>
      </Paper>
      
      <Paper sx={{ p: 2, bgcolor: 'background.paper', height: 600 }}>
        <DataGrid
          rows={filteredLogs}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10, 25, 50]}
          checkboxSelection
          disableSelectionOnClick
          density="standard"
        />
      </Paper>
    </Box>
  );
};

export default DNSLogs;